import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { DetailedCourse } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useIsMobile, useScrollToHash } from "@utils/hooks";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Stack } from "@ui/components/layout";
import { BackButton } from "@ui/components/navigation/backButton";
import { UserView } from "@ui/components/profile/userView";
import { Toolbar } from "@ui/components/toolbar";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { ChapterView } from "./chapterView";
import { CourseNavigation } from "./courseNavigation";
import { CourseChaptersProgress } from "@ui/pages/lms/courseContent/courseProgress/courseChaptersProgress";

interface CourseContentProps extends React.HTMLAttributes<HTMLDivElement> {
	course: DetailedCourse;
	chapterIndex: number;
}
export const CourseContent: React.FC<CourseContentProps> = ({ course, chapterIndex, ...htmlProps }) => {
	const { title, content } = course;
	const chapter = content.chapters.get(chapterIndex);
	const isMobile = useIsMobile();
	const [, setFinished] = useState(false);

	useScrollToHash(20);

	if (!chapter) {
		return null;
	}

	return (
		<>
			<Container {...htmlProps}>
				<StickyContainer>
					{!isMobile && <BackButton text={I18n.t("course.content.back")} path={Path.Lms.ReferenceCourse(course)} />}
					<StyledCourseChaptersProgress courseId={course.id} currentIndex={chapterIndex} />
					<Stack gap={20}>
						<PrimaryMedium>{I18n.t("course.author.label")}</PrimaryMedium>
						<UserView showDescription pictureSize={40} user={getDisplayedAuthor(course)} />
					</Stack>
				</StickyContainer>
				<Content>
					<Title>{title}</Title>
					<ChapterView course={course} chapter={chapter} chapterIndex={chapterIndex} />
					{isMobile ? (
						<CourseNavigation course={course} chapterIndex={chapterIndex} onFinish={() => setFinished(true)} />
					) : (
						<Toolbar>
							<CourseNavigation course={course} chapterIndex={chapterIndex} onFinish={() => setFinished(true)} />
						</Toolbar>
					)}
				</Content>
			</Container>
			{/* <CourseProgressBar chapterIndex={chapterIndex} course={course} finished={finished} /> */}
		</>
	);
};

const Container = styled.div`
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-row-gap: 24px;
	grid-template-areas:
		"info course"
		"info course"
		"info course"
		". course";

	@media ${mobile} {
		grid-template-columns: 100%;

		grid-template-areas:
			"course"
			"info";
	}
`;

const Content = styled.div`
	${gap(40)}
	grid-area: course;
`;

const Title = styled.div`
	margin-top: 8px;
	${({ theme }) => theme.textStyles.title};
`;

const StickyContainer = styled.div`
	${gap(40)};
	grid-area: info;
	@media ${desktop} {
		position: sticky;
		top: 130px;
	}
`;

const StyledCourseChaptersProgress = styled(CourseChaptersProgress)`
	@media ${mobile} {
		padding-top: 20px;
		border-top: 1px solid ${({ theme }) => theme.colors.lightgray};
	}
`;
